/**
 * PATH: packages/tenant-shared/hooks/useDebouncedValue.ts
 * PURPOSE: Generic value-debounce hook used by chatty input fields.
 *
 * WHY: React 19's `useDeferredValue` lets the renderer postpone work
 *      across concurrent updates, but it does NOT prevent the work
 *      from being scheduled at all. For inputs that drive an O(N) sort
 *      over a 700+-row dataset (the Quotes Hub search box) we ALSO
 *      want to coalesce keystrokes so the work is only scheduled
 *      once per typing burst. This hook gives us the second axis of
 *      defense — scheduled-work suppression — while `useDeferredValue`
 *      handles interruptibility of whatever does get scheduled.
 *
 * USAGE:
 *   const [search, setSearch] = useState('')
 *   const debouncedSearch = useDebouncedValue(search, 250)
 *   const filtered = useMemo(() => heavyFilter(rows, debouncedSearch),
 *                            [rows, debouncedSearch])
 */

import { useEffect, useState } from 'react'

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value)

  useEffect(() => {
    if (delayMs <= 0) {
      setDebounced(value)
      return undefined
    }
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}
